<script>
import _ from 'lodash'
import { mapGetters, mapState } from 'vuex'

import { hasPermission, RoleEnum } from '@/misc/permissions/permissionsHelper'

import {
  chunkIsSelected,
  getChunkClasses,
  getFlagOptions,
  tagAsFalseJustification,
  tagAsLegitJustification,
  toggleFalseJustificationTypes
} from '../chunkJustifications'

export default {
  props: {
    report: { type: Object, required: true },
    selection: { type: Object, default: null },
    scrollTarget: { type: Object, default: null },
    isDocumentSource: { type: Boolean, required: false },
    // Redundant with the same feature in the justification popin (ApiJustificationSummary.vue),
    // which already handles it — disabled by default until BUILD-733's follow-up ticket settles
    // where the underlying data should live long-term.
    enableFalseJustificationFeatures: { type: Boolean, default: false },
  },
  emits: ['disable-justification-scrolling'],
  data() {
    return {
      showFalseJustificationTypes: {},
      RoleEnum,
    }
  },
  computed: {
    ...mapGetters(['userRole']),
    ...mapState({
      facilityName: (state) => state.settings.facilityName,
      documentServiceEnable: (state) => state.settings.documentServiceEnable,
    }),
    isDemoFacility() {
      return ['qa-', 'demo-', 'staging-'].some((demoStr) => this.facilityName?.includes(demoStr))
    },
  },
  mounted() {
    if (this.scrollTarget) {
      this.$nextTick(this.scrollToChunk)
    }
  },
  beforeUpdate() {
    if (this.scrollTarget) {
      this.$nextTick(this.scrollToChunk)
    }
  },
  methods: {
    getChunkClasses(chunk, selection) {
      return getChunkClasses(chunk, selection)
    },
    getFlagOptions(chunk) {
      return getFlagOptions(chunk)
    },
    tagAsFalseJustification({ report, healthEntry, chunk, errorType, origin }) {
      this.showFalseJustificationTypes[chunk.hash] = tagAsFalseJustification(report, healthEntry, chunk, errorType, origin, this.$store)
    },
    tagAsLegitJustification({ report, healthEntry, chunk }) {
      this.showFalseJustificationTypes[chunk.hash] = tagAsLegitJustification(report, healthEntry, chunk, this.$store)
    },
    toggleFalseJustificationTypes(chunk) {
      if (this.enableFalseJustificationFeatures) {
        toggleFalseJustificationTypes(chunk, this.showFalseJustificationTypes)
      }
    },
    scrollToChunk() {
      const scrollTarget = this.scrollTarget
      let selectedChunk = null
      let hash = null

      if (scrollTarget && scrollTarget.offset !== null && scrollTarget.docId === this.report.id) {
        selectedChunk = this.report.chunks.find(
          (chunk) => chunk.chunkStart <= scrollTarget.offset && scrollTarget.offset < chunk.chunkEnd
        ) || null
      }

      if (selectedChunk === null) {
        _.forEach(this.report.chunks, (chunk) => {
          if ((selectedChunk === null && chunk.isHighlighted) || chunkIsSelected(chunk, this.selection)) {
            selectedChunk = chunk
          }
        })
      }

      if (selectedChunk !== null) {
        hash = parseInt(selectedChunk.hash)
      }

      if (this.$refs[hash] && selectedChunk !== null) {
        const chunkPosition = window.pageYOffset + this.$refs[hash][0].getBoundingClientRect().top
        const margin = window.innerHeight / 2
        scroll({ top: chunkPosition - margin, behavior: 'smooth' })
      }

      this.$emit('disable-justification-scrolling')

    },
    hasPermission,
  },
}
</script>

<template>
  <div :class="{'highlight-on-click' : isDocumentSource }">
    <div
      class="card-body"
      :data-sancare-id="report.id"
    >
      <div class="row justify-content-between">
        <h1 class="report-title col">
          {{ report.documentTitle }}
        </h1>
        <div class="col-auto">
          <a
            v-if="hasPermission(RoleEnum.ROLE_DOWNLOAD_SOURCEFILE, userRole) && (report.hasOriginalDocument || isDemoFacility || documentServiceEnable)"
            :href="'/api/document-source/' + report.generatedDataId"
            class="report-link"
            target="_blank"
          >
            Voir document <i class="small external icon" />
          </a>
        </div>
      </div>
      <h2
        v-if="report.author"
        class="report-title"
      >
        Auteur : {{ report.author }}
      </h2>
      <h2
        v-if="report.creationDate"
        class="report-title"
      >
        Date : {{ report.creationDate ? report.creationDate.format('L') : '-' }}
      </h2>
    </div>
    <div class="card-body">
      <p
        v-if="report.chunks"
        class="report-content"
      >
        <span
          v-for="chunk in report.chunks"
          :key="chunk.hash"
          :ref="chunk.hash"
          :class="getChunkClasses(chunk, selection)"
          class="text-data-content"
          @click="toggleFalseJustificationTypes(chunk)"
        >{{ chunk.string }}<!--  Comments are here to avoid whitespace issues
        --><v-dropdown
            v-if="(chunk.isHighlighted || chunk.isFalseJustification) && !chunk.isNegative"
            :shown="showFalseJustificationTypes[chunk.hash]"
            placement="bottom-start"
            class="popover-container popover-container__invisible"
            @auto-hide="() => showFalseJustificationTypes[chunk.hash] = false"
          ><!--
        --><template #popper>
              <span
                v-if="!chunk.isFalseJustification"
              >
                Signaler comme :
                <div
                  v-for="option in getFlagOptions(chunk)"
                  :key="option.key"
                >
                  <button
                    class="btn btn-link btn-sm"
                    @click="tagAsFalseJustification({ errorType: option.errorType, origin: option.origin, report, healthEntry: null, chunk })"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </span>
              <span
                v-else
              >
                <button
                  class="btn btn-link btn-sm"
                  @click="tagAsLegitJustification({ report, healthEntry: null, chunk })"
                >
                  Annuler le signalement
                </button>
              </span>
          </template><!--
        --></v-dropdown><!--
      --></span>
      </p>
      <p v-else>
        <span class="text-data-content">{{ report.content }}</span>
      </p>
    </div>
  </div>
</template>
<style>
.report-content {
  overflow-wrap: anywhere;
}
</style>
